/**
 * @fileoverview Workflow bulk-edit helpers (MOR-804)
 *
 * Pure logic behind the workflow editor's bulk-edit dialog: which tasks may be
 * picked as a parent for the current selection, and how one apply rewrites the
 * draft task list. Kept out of the component so the link rules (no self-parent,
 * no parent loop, no self-blocker) can be tested directly.
 *
 * Used by: src/components/settings/tasks/WorkflowEditor.tsx
 */

import type { DraftItem } from "./WorkflowItemCard";

/**
 * One bulk-edit apply. `patch` is the flat field patch written to every selected
 * task; the relationship edits are separate because they either depend on each
 * row's existing value (`blockedBy` in "add" mode) or write onto rows *outside*
 * the selection (`blockingKeys`).
 */
export interface BulkEditChange {
  patch: Partial<DraftItem>;
  /** Blocked-by edit applied to the selected tasks. */
  blockedBy?: { mode: "replace" | "add"; keys: string[] };
  /** Tasks the selection should block: each selected key joins their blockers. */
  blockingKeys?: string[];
}

/**
 * Keys that cannot be the selection's parent: the selected tasks themselves (a
 * self-parent) and everything beneath them, since re-parenting a task under its
 * own descendant closes a parent loop that `replaceItems` rejects.
 */
export function invalidParentKeys(
  items: ReadonlyArray<DraftItem>,
  selectedKeys: ReadonlySet<string>,
): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const it of items) {
    if (!it.parentLocalKey) continue;
    const arr = childrenOf.get(it.parentLocalKey) ?? [];
    arr.push(it.localKey);
    childrenOf.set(it.parentLocalKey, arr);
  }
  const excluded = new Set<string>(selectedKeys);
  const stack = [...selectedKeys];
  while (stack.length > 0) {
    const key = stack.pop() as string;
    for (const child of childrenOf.get(key) ?? []) {
      if (excluded.has(child)) continue;
      excluded.add(child);
      stack.push(child);
    }
  }
  return excluded;
}

/**
 * Apply a bulk edit to the draft task list. The flat patch and the blocked-by
 * edit hit the selected rows; "Blocks" reaches outside the selection, adding
 * every selected key to the blockers of the tasks it names.
 */
export function applyBulkEditToItems(
  items: ReadonlyArray<DraftItem>,
  selectedKeys: ReadonlySet<string>,
  { patch, blockedBy, blockingKeys }: BulkEditChange,
): DraftItem[] {
  const blocking = new Set(blockingKeys ?? []);
  return items.map((it) => {
    const isSelected = selectedKeys.has(it.localKey);
    const withPatch = isSelected ? { ...it, ...patch } : it;

    const withBlockedBy = (): DraftItem => {
      if (!isSelected || !blockedBy) return withPatch;
      // A task can never be its own blocker, whatever the dialog offered.
      const keys = blockedBy.keys.filter((k) => k !== it.localKey);
      return {
        ...withPatch,
        blockedByKeys:
          blockedBy.mode === "replace"
            ? keys
            : Array.from(new Set([...withPatch.blockedByKeys, ...keys])),
      };
    };

    const withBlocking = (): DraftItem => {
      const beforeBlocking = withBlockedBy();
      if (!blocking.has(it.localKey)) return beforeBlocking;
      const added = [...selectedKeys].filter((k) => k !== it.localKey);
      return {
        ...beforeBlocking,
        blockedByKeys: Array.from(
          new Set([...beforeBlocking.blockedByKeys, ...added]),
        ),
      };
    };

    const next = withBlocking();
    // Belt and braces: `replaceItems` rejects a self-parent.
    return next.parentLocalKey === next.localKey
      ? { ...next, parentLocalKey: null }
      : next;
  });
}
